import {
  keepPreviousData,
  useIsFetching,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { hasAnyRole } from '@/shared/guards/roles';
import { useEffectiveRoles } from '@/shared/view-as/use-effective-roles';
import { PENDING_SOURCES } from '../../registry/sources';
import type {
  PendingSource,
  PendingSourceFacet,
} from '../../domain/entities/pending-alert.entity';

const QUERY_KEY = 'pending-alerts';
const STALE_TIME = 1000 * 30;

export interface PendingSourceCard {
  source: PendingSource;
  facets: PendingSourceFacet[];
  /** Soma das facetas — o total de abertos da fonte, sem buscar página. */
  total: number;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Um card por fonte que o usuário pode ver, cada um com a faceta de tipo —
 * que dá o total da badge e as opções do filtro numa chamada só. Card fechado
 * não busca listagem: ver `usePendingSourceItems`.
 *
 * **Gate por papel ANTES do fetch**: fonte sem papel não entra no
 * `useQueries`, então nunca vai à rede — e não rende card. "Sem pendência" e
 * "sem acesso" não podem ficar iguais na tela, por isso quem não pode ver não
 * vê um card vazio.
 *
 * **Sem polling**: o único jeito de atualizar é `refetch()`, do botão
 * "Atualizar" da tela. `sources` é parâmetro para o teste injetar fontes falsas.
 */
export function usePendingSourceFacets(
  sources: PendingSource[] = PENDING_SOURCES,
) {
  const roles = useEffectiveRoles();
  const queryClient = useQueryClient();
  const allowed = sources.filter((source) =>
    hasAnyRole(roles, source.requiredRoles),
  );

  const queries = useQueries({
    queries: allowed.map((source) => ({
      queryKey: [QUERY_KEY, source.key, 'facets'],
      queryFn: () => source.fetchFacets(),
      staleTime: STALE_TIME,
    })),
  });

  const cards: PendingSourceCard[] = allowed.map((source, index) => {
    const result = queries[index]!;
    const facets = result.data ?? [];
    return {
      source,
      facets,
      total: facets.reduce((sum, facet) => sum + facet.count, 0),
      isLoading: result.isLoading,
      isError: result.isError,
    };
  });

  const isFetching = useIsFetching({ queryKey: [QUERY_KEY] }) > 0;

  // Faceta e listagem aberta juntas: depois de "Atualizar", a badge e a lista
  // do card aberto contam a mesma história.
  const refetch = () =>
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

  return { cards, isFetching, refetch };
}

/** Uma página da fonte — só o card aberto chama. */
export function usePendingSourceItems(
  source: PendingSource,
  params: { page: number; perPage: number; type: string },
) {
  return useQuery({
    queryKey: [QUERY_KEY, source.key, 'items', params],
    queryFn: () => source.fetch(params),
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}
